/**
 * Test if a markdown buffer is stable enoug to be parsed.
 * If false, it means the parsing of the buffer might change depending on what comes next.
 */
export function isMarkdownBufferStable(source: string): boolean {
    return findMostStablePartOfMarkdown(source) === source.length;
}

/**
 * Get the index of the most stable part of a markdown source.
 */
export function findMostStablePartOfMarkdown(
    source: string,
    withLiquidProcessed: boolean = false
): number {
    return Math.min(
        // Footnotes are inserted at the end of the document, so we can't parse them.
        findBlockWithFootnoteReference(source) ?? Infinity,
        // Liquid tags in code blocks lead to false positives; but it's safer
        // and sometimes we can optimize later by plugging it in the logic of `processLiquidTags`.
        withLiquidProcessed ? Infinity : (findBlockWithIncompleteLiquidTag(source) ?? Infinity),
        // HTML blocks should be parsed in one go
        findBlockWithIncompleteHtmlTag(source) ?? Infinity,
        source.length
    );
}

/**
 * Find the first footnote reference in the markdown.
 * We can't parse them as footnotes are inserted at the end of the document.
 */
export function findBlockWithFootnoteReference(source: string): number | null {
    const referenceIndex = source.indexOf('[^');
    if (referenceIndex >= 0) {
        return roundToPreviousLine(source, referenceIndex);
    }

    return null;
}

/**
 * Find the first incomplete liquid tag in the input.
 * We don't want to cache after a potentially incomplete liquid tag.
 *
 * Currently liquid tags in code blocks lead to false positives; but it's safer
 * and sometimes we can optimize later by plugging it in the logic of `processLiquidTags`.
 *
 * TODO: improve it to ignore closed liquid tags.
 */
export function findBlockWithIncompleteLiquidTag(source: string): number | null {
    const tagIndex = source.indexOf('{%');
    if (tagIndex >= 0) {
        return roundToPreviousLine(source, tagIndex);
    }

    return null;
}

/**
 * Find the first incomplete HTML tag in the input.
 *
 * TODO: improve to only considered non closed tags.
 */
export function findBlockWithIncompleteHtmlTag(source: string): number | null {
    const tagIndex = source.indexOf('<');
    if (tagIndex >= 0) {
        return roundToPreviousLine(source, tagIndex);
    }

    return null;
}

/**
 * Round the index to the previous line break.
 */
export function roundToPreviousLine(source: string, index: number): number {
    let result = index;
    while (result > 0 && source[result - 1] !== '\n') {
        result--;
    }
    return result;
}

/**
 * Round the index to the next line break.
 */
export function roundToNextLine(source: string, index: number): number {
    let result = index;
    while (result < source.length && source[result] !== '\n') {
        result++;
    }
    return result;
}
